/**
 * Background canvas orchestrator.
 *
 * Layers drawn per frame in order:
 *   1. Sky gradient
 *   2. Aurora / god rays
 *   3. Mountains (back → front, with mouse-driven parallax)
 *   4. Ice chunks (mid-ground drifting polygons)
 *   5. Mist band (near the horizon)
 *   6. Puddles (decorative dark pools)
 *   7. Bokeh orbs (foreground glow)
 *   8. Snow (foreground particles)
 *
 * Single RAF tick via gsap.ticker. Visibility pause. Sustained-FPS degradation.
 */
import { gsap } from 'gsap'
import type { Env } from '../env'
import { COUNTS } from '../config'
import { Sky } from './sky'
import { Snow } from './snow'
import { Aurora } from './aurora'
import { IceChunks } from './iceChunks'
import { Puddles } from './puddles'
import { Mountains } from './mountains'
import { Bokeh } from './bokeh'
import { Mist } from './mist'

export interface BackgroundHandle {
  pause(): void
  resume(): void
  dispose(): void
  isRunning(): boolean
}

export function mountBackground(env: Env): BackgroundHandle {
  const canvasEl = document.getElementById('bg-canvas') as HTMLCanvasElement | null
  if (!canvasEl) throw new Error('#bg-canvas missing from DOM')
  const canvas: HTMLCanvasElement = canvasEl

  const ctxRaw = canvas.getContext('2d', { alpha: false })
  if (!ctxRaw) throw new Error('2d context unavailable')
  const ctx: CanvasRenderingContext2D = ctxRaw

  const sky = new Sky()
  const aurora = new Aurora()
  const mountains = new Mountains()
  const puddles = new Puddles()
  const mist = new Mist()
  const snowCount = env.isMobile ? COUNTS.snowMobile : COUNTS.snowDesktop
  const iceCount = env.isMobile ? COUNTS.iceChunksMobile : COUNTS.iceChunksDesktop
  const snow = new Snow(snowCount)
  const iceChunks = new IceChunks(iceCount)
  const bokeh = new Bokeh(env.isMobile ? 6 : 12)

  const auroraEnabled = !env.reducedMotion
  const mistEnabled = !env.reducedMotion
  const bokehEnabled = !env.reducedMotion

  let cssW = 0
  let cssH = 0
  let running = true

  // Mouse parallax state (for mountains)
  let mouseXNorm = 0
  let targetMouseXNorm = 0

  let slowFrames = 0
  let degraded = false

  function resize() {
    cssW = window.innerWidth
    cssH = window.innerHeight
    const dpr = env.dpr
    canvas.width = Math.floor(cssW * dpr)
    canvas.height = Math.floor(cssH * dpr)
    canvas.style.width = cssW + 'px'
    canvas.style.height = cssH + 'px'
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    sky.resize(cssW, cssH)
    aurora.resize(cssW, cssH)
    mountains.resize(cssW, cssH)
    iceChunks.resize(cssW, cssH)
    puddles.resize(cssW, cssH)
    mist.resize(cssW, cssH)
    snow.resize(cssW, cssH)
    bokeh.resize(cssW, cssH)
  }

  let resizeTimer: number | null = null
  function onResize() {
    if (resizeTimer) window.clearTimeout(resizeTimer)
    resizeTimer = window.setTimeout(resize, 120)
  }
  window.addEventListener('resize', onResize, { passive: true })

  // Mouse parallax: normalize X to -1..1 around center
  function onMouseMove(e: MouseEvent) {
    targetMouseXNorm = (e.clientX / cssW) * 2 - 1
  }
  window.addEventListener('mousemove', onMouseMove, { passive: true })

  resize()

  function tick(_time: number, deltaMs: number) {
    if (!running) return
    const dt = Math.min(deltaMs, 64)

    // FPS watchdog
    if (!degraded && !env.reducedMotion) {
      if (dt > 33) slowFrames++
      else slowFrames = Math.max(0, slowFrames - 1)
      if (slowFrames > 60) {
        degraded = true
        snow.setCount(Math.floor(snowCount / 2))
        iceChunks.setCount(Math.max(2, Math.floor(iceCount / 2)))
      }
    }

    // Lerp parallax
    mouseXNorm += (targetMouseXNorm - mouseXNorm) * 0.05
    mountains.setMouseXNorm(mouseXNorm)

    if (!env.reducedMotion) {
      if (auroraEnabled && !degraded) aurora.tick(dt)
      iceChunks.tick(dt)
      puddles.tick(dt)
      if (mistEnabled) mist.tick(dt)
      if (bokehEnabled) bokeh.tick(dt)
      snow.tick(dt)
    }

    // ── Draw layers ──
    sky.draw(ctx)
    if (auroraEnabled && !degraded) aurora.draw(ctx)
    mountains.draw(ctx)
    iceChunks.draw(ctx)
    if (mistEnabled) mist.draw(ctx)
    puddles.draw(ctx)
    if (bokehEnabled) bokeh.draw(ctx)
    snow.draw(ctx)
  }

  gsap.ticker.add(tick)

  function onVisibility() {
    if (document.hidden) {
      running = false
      gsap.ticker.sleep()
    } else {
      running = true
      gsap.ticker.wake()
    }
  }
  document.addEventListener('visibilitychange', onVisibility)

  return {
    pause() { running = false },
    resume() { running = true },
    isRunning() { return running },
    dispose() {
      gsap.ticker.remove(tick)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('visibilitychange', onVisibility)
      if (resizeTimer) window.clearTimeout(resizeTimer)
    },
  }
}
